import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { authClient } from '../api/client.js';
import { useDictionary, useLocale } from '../i18n/context.js';
import type { Dictionary } from '../i18n/types.js';

// ---------------------------------------------------------------------------
// The persistent application shell: workspace identity, primary navigation,
// and the user menu (UI structure spec section 5). Home, Calendar,
// Campaigns, Projects, Activity, and AI connections are live routes;
// Settings has no screen yet.
//
// Task 6 fix: every one of these seven items used to point `to: '/home'`,
// including the six that had no screen -- a link that silently landed
// wherever the fallback route did, promising a product surface that did
// not exist. A live item still renders as a `Link` (so `href` carries its
// real destination and back/forward/open-in-new-tab all work); an
// unimplemented item renders as a disabled `<button>` instead -- a control
// that visibly admits it is not ready, never a link that quietly lies about
// where it goes.
//
// Shell only ever renders once `App.tsx`'s session gate has confirmed a live
// session, so `useSession()` here is read-only display (the signed-in
// email) plus the sign-out action -- it never has to handle the "no
// session" case itself. `authClient.signOut()` clears the session cookie on
// the Worker; the shared session store that produces means App's own
// `useSession()` re-renders right after, taking the operator back to
// `/sign-in` with no explicit navigation call needed here (structure spec
// 5.3's "the application has no signed-out landing page" no longer holds --
// `/sign-in` is that landing page now).
// ---------------------------------------------------------------------------

// Each live item names its own route; an unimplemented item carries no
// `to` at all, so there is no destination for a future refactor to
// accidentally start using.
const NAV_ITEMS: Array<{ key: string; label: (d: Dictionary) => string; to: string | null }> = [
  { key: 'home', label: (d) => d.nav.home, to: '/home' },
  { key: 'calendar', label: (d) => d.nav.calendar, to: '/calendar' },
  { key: 'campaigns', label: (d) => d.nav.campaigns, to: '/campaigns' },
  { key: 'projects', label: (d) => d.nav.projects, to: '/projects' },
  { key: 'activity', label: (d) => d.nav.activity, to: '/activity' },
  { key: 'aiConnections', label: (d) => d.nav.aiConnections, to: '/ai-connections' },
  { key: 'settings', label: (d) => d.nav.settings, to: null },
];

export const Shell = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const dict = useDictionary();
  const [locale, setLocale] = useLocale();
  const { data: session } = authClient.useSession();

  return (
    <div className="shell">
      <nav className="rail">
        <div className="stack-1">
          <div className="rail__brand">tomfuru</div>
          <div className="t-meta">{dict.ui.workspaceAllProjects}</div>
        </div>

        <div className="rail__nav">
          {NAV_ITEMS.map((item) => (
            item.to !== null
              ? (
                <Link key={item.key} className="rail__item" to={item.to}>
                  <span>{item.label(dict)}</span>
                </Link>
              )
              : (
                <button
                  key={item.key}
                  type="button"
                  className="rail__item"
                  disabled
                  title={dict.ui.navNotYetAvailable}
                  style={{ opacity: 0.5, cursor: 'not-allowed', textAlign: 'left' }}
                >
                  <span>{item.label(dict)}</span>
                </button>
              )
          ))}
        </div>

        <div className="stack-1" style={{ marginTop: 'auto' }}>
          {session?.user.email && <span className="t-meta">{dict.ui.signedInAs(session.user.email)}</span>}
          <button
            type="button"
            className="btn btn--ghost"
            data-testid="locale-toggle"
            onClick={() => setLocale(locale === 'en' ? 'ja' : 'en')}
          >
            {locale === 'en' ? '日本語' : 'English'}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            data-testid="sign-out"
            onClick={() => { void authClient.signOut(); }}
          >
            {dict.ui.auth.signOut}
          </button>
        </div>
      </nav>

      <div className="shell__main">{children}</div>
    </div>
  );
};
